/**
 * AdrBlockEditor - compact BlockNote wrapper for ADR content fields.
 *
 * Uses ref-based pattern (like TaskModal): parent reads editor.document
 * at save time via onEditorReady ref. No onChange state syncing.
 * Remount via React `key` prop when switching ADRs.
 */

import { useEffect } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';

interface AdrBlockEditorProps {
  initialContent: unknown[];
  onEditorReady: (editor: unknown) => void;
  onContentChange?: () => void;
  placeholder?: string;
}

export function AdrBlockEditor({ initialContent, onEditorReady, onContentChange, placeholder }: AdrBlockEditorProps) {
  const editor = useCreateBlockNote({
    initialContent: initialContent.length > 0 ? (initialContent as any) : undefined,
  });

  useEffect(() => {
    onEditorReady(editor);
  }, [editor, onEditorReady]);

  return (
    <div data-adr-bn-field className="min-h-[48px]">
      <style>{`
/* Kill all padding on editor and blocks */
[data-adr-bn-field] .bn-editor {
  padding: 0 !important;
  font-size: 12px !important;
}
[data-adr-bn-field] .bn-block-group { padding: 0 !important; margin: 0 !important; }
[data-adr-bn-field] .bn-block-outer { padding: 0 !important; margin: 0 !important; }
[data-adr-bn-field] .bn-block { padding: 1px 0 !important; }
[data-adr-bn-field] .bn-inline-content { padding: 0 !important; }

/* Hide + add block button (first child in side menu) */
[data-adr-bn-field] .bn-side-menu > :first-child { display: none !important; }

/* Compact side menu: overlay on content from left edge */
[data-adr-bn-field] .bn-side-menu {
  gap: 0 !important;
  left: 0 !important;
}

/* Smaller drag handle */
[data-adr-bn-field] .bn-side-menu .mantine-UnstyledButton-root {
  width: 14px !important;
  height: 16px !important;
  min-width: 14px !important;
  min-height: 16px !important;
  padding: 0 !important;
  opacity: 0.4;
}
[data-adr-bn-field] .bn-side-menu .mantine-UnstyledButton-root:hover { opacity: 1; }
[data-adr-bn-field] .bn-side-menu svg {
  width: 10px !important;
  height: 10px !important;
}

/* Compact drag handle dropdown / context menu */
[data-adr-bn-field] .bn-drag-handle-menu,
[data-adr-bn-field] .mantine-Menu-dropdown {
  font-size: 11px !important;
  min-width: 120px !important;
  padding: 2px !important;
}
[data-adr-bn-field] .mantine-Menu-item {
  font-size: 11px !important;
  padding: 3px 6px !important;
  min-height: unset !important;
}
[data-adr-bn-field] .mantine-Menu-itemIcon {
  width: 14px !important;
  height: 14px !important;
  margin-right: 4px !important;
}
[data-adr-bn-field] .mantine-Menu-itemIcon svg {
  width: 12px !important;
  height: 12px !important;
}
[data-adr-bn-field] .mantine-Menu-itemLabel {
  font-size: 11px !important;
}
[data-adr-bn-field] .mantine-Menu-itemRightSection {
  font-size: 9px !important;
}

/* Compact formatting toolbar */
[data-adr-bn-field] .bn-toolbar,
[data-adr-bn-field] .mantine-Toolbar-root {
  padding: 2px !important;
  gap: 1px !important;
}
[data-adr-bn-field] .bn-toolbar button,
[data-adr-bn-field] .bn-toolbar .mantine-UnstyledButton-root {
  width: 22px !important;
  height: 22px !important;
  min-width: 22px !important;
  min-height: 22px !important;
  padding: 2px !important;
}
[data-adr-bn-field] .bn-toolbar svg {
  width: 13px !important;
  height: 13px !important;
}
      `}</style>
      <BlockNoteView editor={editor} theme="light" data-placeholder={placeholder} onChange={onContentChange} />
    </div>
  );
}
